import { useState, useEffect } from 'react';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showAuditPopup, setShowAuditPopup] = useState(false);
  const [showServicesPopup, setShowServicesPopup] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);

  const words = ['Design', 'Build', 'Launch', 'Maintain', 'Improve'];

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Rotating word animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, 1500); // Changed from 2000ms to 1500ms for faster animation

    return () => clearInterval(interval);
  }, [words.length]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen md:min-h-screen sm:min-h-[100vh] flex items-start sm:items-center justify-center overflow-hidden">
      {/* Seamless Flowing Background - Fixed blockiness */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/20 to-black"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-blue-950/15 to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-bl from-purple-900/10 via-transparent to-blue-900/10"></div>

      {/* Animated Floating Orbs - Seamless blending */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large Purple Orb */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-gradient-to-r from-purple-600/20 to-blue-500/15 rounded-full blur-3xl animate-float-slow"></div>

        {/* Medium Blue Orb */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-r from-blue-500/18 to-cyan-400/12 rounded-full blur-3xl animate-float-fast"></div>

        {/* Small Purple Orb */}
        <div className="absolute bottom-32 left-1/4 w-48 h-48 bg-gradient-to-r from-violet-600/15 to-purple-400/10 rounded-full blur-3xl animate-float-reverse"></div>

        {/* Additional Orbs for seamless depth */}
        <div
          className="absolute bottom-0 right-0 w-72 h-72 bg-gradient-to-r from-indigo-500/12 to-blue-600/8 rounded-full blur-3xl animate-float-slow"
          style={{ animationDelay: '5s' }}
        ></div>
        <div
          className="absolute top-1/2 left-10 w-32 h-32 bg-gradient-to-r from-cyan-400/20 to-blue-300/15 rounded-full blur-3xl animate-float-fast"
          style={{ animationDelay: '3s' }}
        ></div>
      </div>

      {/* Seamless Flowing Wave Elements - Removed harsh lines */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 w-full h-48 bg-gradient-to-r from-transparent via-purple-500/8 to-transparent blur-sm animate-wave-slow" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-3/4 w-full h-48 bg-gradient-to-r from-transparent via-blue-500/6 to-transparent blur-sm animate-wave-fast" style={{ animationDelay: '7s' }}></div>
        <div className="absolute top-1/2 w-full h-48 bg-gradient-to-r from-transparent via-indigo-500/7 to-transparent blur-sm animate-wave-slow" style={{ animationDelay: '14s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-start lg:items-center min-h-screen md:min-h-screen sm:min-h-[100vh] pt-16 sm:pt-4 md:pt-20 pb-4 sm:pb-2 md:pb-12">
          {/* Left Column - Content */}
          <div className="space-y-2 sm:space-y-2 md:space-y-8">
            {/* Main Headline - H1 for SEO - Left-aligned on mobile */}
            <div
              className={`space-y-1 sm:space-y-1 md:space-y-4 transition-all duration-1000 delay-300 text-left ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                <span className="block bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent pb-1 sm:pb-2">
                  We{' '}
                  <span className="inline-block min-w-[180px] sm:min-w-[200px] md:min-w-[240px] lg:min-w-[280px] text-left align-top">
                    <span className="bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent word-rotate-animation">
                      {words[wordIndex]}
                    </span>
                  </span>
                </span>
                <span className="block bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent pb-1 sm:pb-2">
                  Your Website
                </span>
                <span className="block bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent">
                  - So You Don't Have To
                </span>
              </h1>
            </div>

            {/* Subtitle - Different text for mobile vs desktop */}
            <p
              className={`text-sm sm:text-base md:text-xl text-gray-300 max-w-xl leading-relaxed transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Mobile text - shorter version */}
              <span className="sm:hidden">
                We provide a fully managed website service that covers every stage, from thoughtful design and expert development to hosting, security, and ongoing optimisation.
              </span>
              {/* Desktop text - full version */}
              <span className="hidden sm:inline">
                We provide a fully managed website service that covers every stage, from thoughtful design and expert development to hosting, security, and ongoing optimisation. Everything works seamlessly, so you can focus on growing your business with confidence.
              </span>
            </p>

            {/* CTA Buttons - Mobile capsule container, desktop direct layout */}
            <div
              className={`transition-all duration-1000 delay-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Mobile: Capsule container */}
              <div className="sm:hidden bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-3 max-w-md">
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => scrollToSection('free-audit')}
                    className="group relative px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl text-sm hover:from-purple-500 hover:to-blue-400 transition-all duration-300 transform hover:scale-105 enhanced-pulse-glow whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black"
                    aria-label="Get Your Free Website Audit"
                  >
                    <span className="relative z-10">Get Your Free Website Audit</span>
                  </button>

                  <button
                    onClick={() => setShowServicesPopup(true)}
                    className="group px-3 py-2 bg-white/5 backdrop-blur-sm border border-white/10 text-white/70 font-medium rounded-xl text-sm hover:bg-white/10 hover:border-purple-400/30 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black"
                    aria-label="Explore Our Services"
                  >
                    Explore Services
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                  </button>
                </div>
              </div>

              {/* Desktop: Direct button layout without capsule */}
              <div className="hidden sm:flex flex-col sm:flex-row gap-3 md:gap-4 max-w-md">
                <button
                  onClick={() => scrollToSection('free-audit')}
                  className="group relative px-4 py-2.5 md:px-8 md:py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl text-base md:text-lg hover:from-purple-500 hover:to-blue-400 transition-all duration-300 transform hover:scale-105 enhanced-pulse-glow whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black"
                  aria-label="Get Your Free Website Audit"
                >
                  <span className="relative z-10">Get Your Free Website Audit</span>
                </button>

                <button
                  onClick={() => setShowServicesPopup(true)}
                  className="group px-4 py-2.5 md:px-8 md:py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white/70 font-medium rounded-xl text-base hover:bg-white/10 hover:border-purple-400/30 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black"
                  aria-label="Explore Our Services"
                >
                  Explore Services
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                </button>
              </div>
            </div>

            {/* Mobile Secondary Visual Element - Moved directly below buttons */}
            <div 
              className={`lg:hidden flex justify-center items-center pt-3 transition-all duration-1500 delay-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <MobileHeroGraphic />
            </div>

            {/* Enhanced Mobile Scroll indicator - Moved directly below graphic */}
            <div className="lg:hidden flex flex-col items-center pt-2">
              <div className="flex flex-col items-center space-y-1">
                <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
                  <div className="w-1 h-3 bg-gradient-to-b from-purple-400 to-blue-400 rounded-full mt-2 animate-bounce"></div>
                </div>
                <p className="text-xs text-gray-400 animate-pulse">Scroll to explore</p>
              </div>
            </div>
          </div>

          {/* Right Column - Desktop Internet Graphic */}
          <div
            className={`relative hidden lg:flex justify-center items-center transition-all duration-1500 delay-800 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <InternetGraphic />
          </div>
        </div>
      </div>

      {/* Desktop Scroll indicator */}
      <div className="hidden lg:block absolute bottom-2 sm:bottom-2 md:bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex flex-col items-center space-y-1">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gradient-to-b from-purple-400 to-blue-400 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </div>

      {/* Services Popup */}
      {showServicesPopup && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-lg rounded-3xl p-6 md:p-8 max-w-3xl w-full max-h-[85vh] overflow-y-auto border border-purple-500/20">
            <div className="flex justify-between items-center mb-6 md:mb-8">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                Our Premium Services
              </h3>
              <button
                onClick={() => setShowServicesPopup(false)}
                className="text-gray-400 hover:text-white text-2xl cursor-pointer transition-colors duration-300 w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-2xl p-5 md:p-6 hover:from-purple-500/15 hover:to-purple-600/10 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-palette-line text-white text-xl"></i>
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-white mb-3">Website Design &amp; Launch</h4>
                <p className="text-gray-300 mb-4 text-sm">
                  Custom WordPress websites designed to convert visitors into customers.
                </p>
                <ul className="text-sm text-gray-400 space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-purple-400"></i> Mobile-responsive design
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-purple-400"></i> Fast loading optimisation
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-purple-400"></i> SEO-friendly structure
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-purple-400"></i> Brand-aligned styling
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-2xl p-5 md:p-6 hover:from-blue-500/15 hover:to-blue-600/10 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-white text-xl"></i>
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-white mb-3">Conversion Optimisation</h4>
                <p className="text-gray-300 mb-4 text-sm">
                  Strategic layout and content optimisation to maximise conversions.
                </p>
                <ul className="text-sm text-gray-400 space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-blue-400"></i> A/B tested layouts
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-blue-400"></i> Strategic CTA placement
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-blue-400"></i> User experience optimisation
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-blue-400"></i> Analytics integration
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 rounded-2xl p-5 md:p-6 hover:from-cyan-500/15 hover:to-cyan-600/10 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-calendar-line text-white text-xl"></i>
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-white mb-3">Booking &amp; Payments</h4>
                <p className="text-gray-300 mb-4 text-sm">
                  Seamless appointment booking and secure payment integration.
                </p>
                <ul className="text-sm text-gray-400 space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-cyan-400"></i> Online appointment system
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-cyan-400"></i> Payment gateway setup
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-cyan-400"></i> Automated confirmations
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-cyan-400"></i> Calendar synchronisation
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-2xl p-5 md:p-6 hover:from-emerald-500/15 hover:to-emerald-600/10 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-tools-line text-white text-xl"></i>
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-white mb-3">Ongoing Management</h4>
                <p className="text-gray-300 mb-4 text-sm">
                  Monthly updates, security, and performance optimisation.
                </p>
                <ul className="text-sm text-gray-400 space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-400"></i> Regular content updates
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-400"></i> Security monitoring
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-400"></i> Performance optimisation
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-emerald-400"></i> Priority support
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => {
                  setShowServicesPopup(false);
                  scrollToSection('free-audit');
                }}
                className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-500 hover:to-blue-400 transition-all duration-300 transform hover:scale-105 cursor-pointer text-sm md:text-base"
              >
                Start Your Free Website Audit
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes word-rotate {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          10% {
            opacity: 1;
            transform: translateY(0);
          }
          90% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
          }
        }
        
        .word-rotate-animation {
          animation: word-rotate 1.5s ease-in-out;
        }
      `}</style>
    </section>
  );
};

// Desktop Internet Graphic Component
const InternetGraphic = () => {
  return (
    <div className="relative w-full max-w-2xl mx-auto">
      {/* Luxurious Minimalist Graphic */}
      <div className="relative animate-fade-in-slow">
        {/* Elegant geometric shapes with premium feel */}
        <div className="relative aspect-square w-full max-w-[500px] mx-auto">
          {/* Main central circle with gradient border */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-64 h-64 rounded-full border-2 border-purple-500/30 animate-pulse-slow"></div>
          </div>
          
          {/* Rotating outer ring */}
          <div className="absolute inset-0 flex items-center justify-center animate-spin-slow">
            <div className="w-80 h-80 rounded-full border border-blue-400/20"></div>
          </div>
          
          {/* Inner glowing core */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500/40 to-blue-500/40 blur-2xl animate-pulse-glow"></div>
          </div>
          
          {/* Continuously rotating icons container - Perfect circular path */}
          <div className="absolute inset-0 animate-rotate-continuous">
            {/* Using precise circular coordinates for perfect circular motion */}
            
            {/* Website Icon - 0 degrees (top) */}
            <div className="absolute" style={{ 
              top: 'calc(50% - 160px)', 
              left: '50%', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600/90 to-purple-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-purple-500/50 border border-purple-400/30 animate-counter-rotate">
                <i className="ri-window-line text-white text-2xl"></i>
              </div>
            </div>
            
            {/* Speed Icon - 45 degrees (top-right) */}
            <div className="absolute" style={{ 
              top: 'calc(50% - 113px)', 
              left: 'calc(50% + 113px)', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-600/90 to-blue-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-blue-500/50 border border-blue-400/30 animate-counter-rotate">
                <i className="ri-flashlight-line text-white text-2xl"></i>
              </div>
            </div>
            
            {/* Analytics Icon - 90 degrees (right) */}
            <div className="absolute" style={{ 
              top: '50%', 
              left: 'calc(50% + 160px)', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-600/90 to-cyan-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-cyan-500/50 border border-cyan-400/30 animate-counter-rotate">
                <i className="ri-line-chart-line text-white text-2xl"></i>
              </div>
            </div>
            
            {/* Security Icon - 135 degrees (bottom-right) */}
            <div className="absolute" style={{ 
              top: 'calc(50% + 113px)', 
              left: 'calc(50% + 113px)', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-600/90 to-violet-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-violet-500/50 border border-violet-400/30 animate-counter-rotate">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
            </div>
            
            {/* Mobile Icon - 180 degrees (bottom) */}
            <div className="absolute" style={{ 
              top: 'calc(50% + 160px)', 
              left: '50%', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600/90 to-indigo-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-indigo-500/50 border border-indigo-400/30 animate-counter-rotate">
                <i className="ri-smartphone-line text-white text-2xl"></i>
              </div>
            </div>
            
            {/* SEO Icon - 225 degrees (bottom-left) */}
            <div className="absolute" style={{ 
              top: 'calc(50% + 113px)', 
              left: 'calc(50% - 113px)', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-600/90 to-pink-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-purple-500/50 border border-purple-400/30 animate-counter-rotate">
                <i className="ri-search-line text-white text-2xl"></i>
              </div>
            </div>
            
            {/* Design Icon - 270 degrees (left) */}
            <div className="absolute" style={{ 
              top: '50%', 
              left: 'calc(50% - 160px)', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/90 to-purple-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-blue-500/50 border border-blue-400/30 animate-counter-rotate">
                <i className="ri-palette-line text-white text-2xl"></i>
              </div>
            </div>
            
            {/* Conversion Icon - 315 degrees (top-left) */}
            <div className="absolute" style={{ 
              top: 'calc(50% - 113px)', 
              left: 'calc(50% - 113px)', 
              transform: 'translate(-50%, -50%)' 
            }}>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-emerald-500/50 border border-emerald-400/30 animate-counter-rotate">
                <i className="ri-arrow-up-circle-line text-white text-2xl"></i>
              </div>
            </div>
          </div>
          
          {/* Subtle connecting lines */}
          <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 400">
            <circle cx="200" cy="200" r="120" fill="none" stroke="url(#gradient1)" strokeWidth="1" strokeDasharray="4 4" className="animate-dash" />
            <circle cx="200" cy="200" r="160" fill="none" stroke="url(#gradient2)" strokeWidth="1" strokeDasharray="6 6" className="animate-dash-reverse" />
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
              <linearGradient id="gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Glow Effects */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse-slow"></div>
      </div>

      <style>{`
        @keyframes fade-in-slow {
          0% {
            opacity: 0;
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes rotate-continuous {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes counter-rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(-360deg);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.1);
          }
        }

        @keyframes custom-pulse-glow {
          0%, 100% {
            box-shadow: 0 0 15px rgba(139, 92, 246, 0.6), 0 0 30px rgba(59, 130, 246, 0.4);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 25px rgba(139, 92, 246, 0.8), 0 0 50px rgba(59, 130, 246, 0.6);
            transform: scale(1.02);
          }
        }
        
        @keyframes dash {
          to {
            stroke-dashoffset: -100;
          }
        }
        
        @keyframes dash-reverse {
          to {
            stroke-dashoffset: 100;
          }
        }
        
        .animate-fade-in-slow {
          animation: fade-in-slow 2s ease-out forwards;
        }
        
        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
        }
        
        .animate-rotate-continuous {
          animation: rotate-continuous 20s linear infinite;
        }

        .animate-counter-rotate {
          animation: counter-rotate 20s linear infinite;
        }
        
        .custom-pulse-glow {
          animation: custom-pulse-glow 4s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 4s ease-in-out infinite;
        }
        
        .animate-dash {
          animation: dash 20s linear infinite;
        }
        
        .animate-dash-reverse {
          animation: dash-reverse 25s linear infinite;
        }
      `}</style>
    </div>
  );
};

// Mobile Hero Graphic Component
const MobileHeroGraphic = () => {
  return (
    <div className="relative w-full max-w-[200px] mx-auto">
      <div className="relative aspect-square w-full">
        {/* Central glowing core */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/50 to-blue-500/50 blur-xl animate-pulse-glow"></div>
        </div>
        
        {/* Simplified rotating ring */}
        <div className="absolute inset-0 flex items-center justify-center animate-spin-slow">
          <div className="w-36 h-36 rounded-full border border-purple-400/20"></div>
        </div>
        
        {/* Floating icons in triangular formation */}
        <div className="absolute inset-0 animate-float-cluster">
          {/* Top icon - Website */}
          <div className="absolute" style={{ 
            top: 'calc(50% - 45px)', 
            left: '50%', 
            transform: 'translate(-50%, -50%)' 
          }}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600/80 to-purple-500/80 backdrop-blur-sm flex items-center justify-center shadow-xl shadow-purple-500/40 border border-purple-400/20">
              <i className="ri-window-line text-white text-lg"></i>
            </div>
          </div>
          
          {/* Bottom left icon - Design */}
          <div className="absolute" style={{ 
            top: 'calc(50% + 30px)', 
            left: 'calc(50% - 38px)', 
            transform: 'translate(-50%, -50%)' 
          }}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600/80 to-blue-500/80 backdrop-blur-sm flex items-center justify-center shadow-xl shadow-blue-500/40 border border-blue-400/20">
              <i className="ri-palette-line text-white text-lg"></i>
            </div>
          </div>
          
          {/* Bottom right icon - Analytics */}
          <div className="absolute" style={{ 
            top: 'calc(50% + 30px)', 
            left: 'calc(50% + 38px)', 
            transform: 'translate(-50%, -50%)' 
          }}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-600/80 to-cyan-500/80 backdrop-blur-sm flex items-center justify-center shadow-xl shadow-cyan-500/40 border border-cyan-400/20">
              <i className="ri-line-chart-line text-white text-lg"></i>
            </div>
          </div>
        </div>
        
        {/* Subtle connecting lines */}
        <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 200 200">
          <circle cx="100" cy="100" r="60" fill="none" stroke="url(#mobileGradient1)" strokeWidth="1" strokeDasharray="3 3" className="animate-dash" />
          <defs>
            <linearGradient id="mobileGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Mobile glow effects */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-gradient-to-r from-purple-500/15 to-blue-500/15 rounded-full blur-2xl animate-pulse-slow"></div>
      </div>

      <style>{`
        @keyframes float-cluster {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-8px) rotate(180deg);
          }
        }
        
        .animate-float-cluster {
          animation: float-cluster 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HeroSection;
